const statusText = (value) => String(value ?? '').trim().toUpperCase();
const validId = (value) => /^[1-9]\d*$/.test(String(value ?? ''));

// Explicit UNMAPPED is a contractual exception; a missing/null key is not.
export function hasUnmappedUnmeContract(value) {
  if (!value || typeof value !== 'object') return false;
  const isUnmapped = (row) => ['contractKey', 'contract_key', 'contractId', 'contract_id']
    .some((field) => statusText(row?.[field]) === 'UNMAPPED');
  if (isUnmapped(value)) return true;
  return Array.isArray(value.contracts) && value.contracts.some(isUnmapped);
}

// Amounts come from a decimal database column. Work in hundredths so that
// summing several strings never introduces floating-point rounding errors.
function amountInHundredths(value) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const raw = String(value).trim();
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(raw);
  if (!match || (match[3] || '').slice(2).replace(/0/g, '')) return null;
  const whole = Number(match[2]);
  const fraction = Number((match[3] || '').slice(0, 2).padEnd(2, '0'));
  const magnitude = whole * 100 + fraction;
  if (!Number.isSafeInteger(magnitude)) return null;
  return match[1] ? -magnitude : magnitude;
}

/**
 * Initial payment and a retry are deliberately separate actions. In
 * particular, a READY row with canRetry=true must never join auto-selected
 * initial payments. The server still revalidates all payout conditions.
 */
export function getUnmePayoutDecision(status, contractSettlement, settlementId) {
  const parentStatus = statusText(status);
  const blocked = (reason) => ({ selectable: false, retryable: false, amount: null, reason });
  if (hasUnmappedUnmeContract(contractSettlement)) return blocked('계약 정보 확인 필요');
  if (!['READY', 'TRANSFER_FAILED'].includes(parentStatus)) return blocked('상태 확인 필요');

  // Old servers and historic non-aggregated rows keep the existing READY flow.
  if (contractSettlement == null || (contractSettlement.available === false && ['NOT_AGGREGATED', 'SCHEMA_NOT_READY'].includes(contractSettlement.reason))) {
    return parentStatus === 'READY'
      ? { selectable: true, retryable: false, amount: null, reason: null }
      : blocked('계약별 지급 정보를 확인할 수 없습니다.');
  }

  const data = contractSettlement;
  if (data.available !== true || data.reconciled !== true || data.requiresResultCheck !== false || !Array.isArray(data.contracts) || !data.contracts.length) {
    return blocked('계약별 지급 정보를 확인해 주세요.');
  }

  const ids = new Set();
  const keys = new Set();
  let retryHundredths = 0;
  let retryCount = 0;
  let initialCount = 0;
  let completedCount = 0;
  for (const contract of data.contracts) {
    const key = statusText(contract?.contractKey);
    const contractId = String(contract?.id ?? '');
    const payoutStatus = statusText(contract?.payoutStatus);
    const netAmount = amountInHundredths(contract?.net_amount);
    if ((key && !['PRIMARY', 'SECONDARY'].includes(key)) || (key && keys.has(key)) || !validId(contractId) || ids.has(contractId) || netAmount === null || contract.requiresResultCheck !== false || typeof contract.canRetry !== 'boolean') {
      return blocked('계약별 지급 정보를 확인해 주세요.');
    }
    if (settlementId != null && (!validId(settlementId) || String(contract.settlement_id ?? '') !== String(settlementId))) {
      return blocked('정산과 계약 정보가 일치하지 않습니다.');
    }
    if (key) keys.add(key);
    ids.add(contractId);

    if (netAmount <= 0) {
      if (payoutStatus !== 'NOT_REQUIRED' || contract.canRetry || contract.latestPayout != null) return blocked('지급 대상 금액을 확인해 주세요.');
      continue;
    }
    if (payoutStatus === 'NOT_REQUESTED') {
      if (contract.canRetry || contract.latestPayout != null) return blocked('지급 상태를 확인해 주세요.');
      initialCount += 1;
      continue;
    }
    if (!['SUCCESS', 'DECLINED'].includes(payoutStatus)) return blocked('지급 결과 확인이 필요합니다.');
    const payout = contract.latestPayout;
    if (!payout || !validId(payout.id) || String(payout.contract_id ?? '') !== contractId || statusText(payout.status) !== payoutStatus || (settlementId != null && String(payout.settlement_id ?? '') !== String(settlementId))) {
      return blocked('최신 지급 이력을 확인해 주세요.');
    }
    if (payoutStatus === 'SUCCESS') {
      if (contract.canRetry) return blocked('지급 상태를 확인해 주세요.');
      completedCount += 1;
    } else {
      if (!contract.canRetry) return blocked('재신청 가능 여부를 확인해 주세요.');
      retryHundredths += netAmount;
      if (!Number.isSafeInteger(retryHundredths)) return blocked('재신청 금액을 확인해 주세요.');
      retryCount += 1;
    }
  }

  if (data.canRetry === true && retryCount > 0 && initialCount === 0) {
    return { selectable: false, retryable: true, amount: retryHundredths / 100, reason: null };
  }
  if (data.canRetry === false && parentStatus === 'READY' && initialCount > 0 && retryCount === 0 && completedCount === 0) {
    return { selectable: true, retryable: false, amount: null, reason: null };
  }
  return blocked('지급 상태를 확인해 주세요.');
}

export function isUnmePayoutSelectable(status, contractSettlement, settlementId) {
  return getUnmePayoutDecision(status, contractSettlement, settlementId).selectable;
}

// Keep a pending retry locked until every requested failed contract shows a new
// attempt or an authoritative status transition. Amount-only or contract-list
// changes do not prove that a submitted request has finished processing.
export function snapshotUnmeRetryAttempts(contractSettlement, settlementId) {
  if (!validId(settlementId) || !Array.isArray(contractSettlement?.contracts)) return null;
  const identities = [];
  const targets = [];
  const seen = new Set();
  for (const contract of contractSettlement.contracts) {
    const id = String(contract?.id ?? '');
    const key = statusText(contract?.contractKey);
    if (!validId(id) || (key && !['PRIMARY', 'SECONDARY'].includes(key)) || seen.has(id) || String(contract?.settlement_id ?? '') !== String(settlementId)) return null;
    seen.add(id);
    identities.push(`${id}:${key}`);
    if (statusText(contract.payoutStatus) === 'DECLINED' && contract.canRetry === true) {
      const payout = contract.latestPayout;
      if (!validId(payout?.id) || String(payout?.contract_id ?? '') !== id || String(payout?.settlement_id ?? '') !== String(settlementId) || statusText(payout?.status) !== 'DECLINED') return null;
      targets.push({ id, payoutId: String(payout.id) });
    }
  }
  if (!targets.length) return null;
  return { identities: identities.sort(), targets, settlementId: String(settlementId) };
}

export function hasObservedUnmeRetryAttempts(snapshot, contractSettlement) {
  if (!snapshot || contractSettlement?.available !== true || !Array.isArray(contractSettlement.contracts)) return false;
  const contracts = contractSettlement.contracts;
  const identities = contracts.map((contract) => `${String(contract?.id ?? '')}:${statusText(contract?.contractKey)}`).sort();
  if (identities.length !== snapshot.identities.length || identities.some((item, index) => item !== snapshot.identities[index])) return false;
  return snapshot.targets.every((target) => {
    const matches = contracts.filter((contract) => String(contract?.id ?? '') === target.id);
    if (matches.length !== 1) return false;
    const contract = matches[0];
    const payout = contract.latestPayout;
    const status = statusText(contract.payoutStatus);
    if (String(contract.settlement_id ?? '') !== snapshot.settlementId || !['DECLINED', 'SUCCESS', 'UNKNOWN', 'SENDING'].includes(status) ||
      !validId(payout?.id) || String(payout?.contract_id ?? '') !== target.id || String(payout?.settlement_id ?? '') !== snapshot.settlementId || statusText(payout?.status) !== status) return false;
    return String(payout.id) !== target.payoutId || status !== 'DECLINED';
  });
}

export function unmePayoutRequestMessage() {
  return '지급 요청을 처리했습니다. 정산 내역에서 계약별 지급 결과를 확인해 주세요.';
}
